// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { ButtonIconPresentationProps, ButtonPresentationProps } from "@insite/mobius/Button";
import { FormFieldStyle } from "@insite/mobius/FormField";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps, GridItemStyle } from "@insite/mobius/GridItem";
import { IconWrapper } from "@insite/mobius/Icon";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { DivWrapper } from "@werner/XDL/constants";
import CaretRight from "@werner/XDL/Icons/CaretRight";
import { color, xScreen } from "@werner/XDL/Utilities";
import { css } from "styled-components";

interface MyAccountFilterSectionStyles {
    /** GridContainer for countGridItem and toggleFilterGridItem */
    filterContainer?: GridContainerProps;
    /** Filter fields GridItem */
    fieldGridItem?: GridItemProps;
    /** Count orders GridItem */
    resultCountTextStyles?: TypographyPresentationProps;
    /** Filter clickable GridItem */
    toggleFilterGridItem?: GridItemProps;
    /** Toggle filter Button */
    toggleFilterButton?: ButtonPresentationProps;
    /** Search Form Styles */
    searchFormStyles: {
        wrapper?: InjectableCss;
        headingText?: TypographyPresentationProps;
    };
    /** Clear Button Styles */
    clearButton?: ButtonPresentationProps;
}

const myAccountFilterSectionStyles: MyAccountFilterSectionStyles = {
    filterContainer: {
        gap: 8,
        css: css`
            margin-bottom: 20px;
        `
    },
    fieldGridItem: {
        width: [12, 12, 4, 4, 4],
    },
    resultCountTextStyles: {
        size: 24,
        lineHeight: "30px",
        weight: 500,
        transform: "capitalize",
    },
    toggleFilterGridItem: {
        width: [12, 6, 6, 6, 6],
        css: css`
            order: 1;
            ${xScreen("lte", "xs")} {
                order: 2;
            }
        `,
    },
    toggleFilterButton: {
        shape: "rectangle",
        buttonType: "solid",
        shadow: false,
        color: "common.white",
        css: css`
            height: 40px;
            padding: 15px 10px;
            border: 1px solid ${color("common.ui5")};
            border-radius: 0;
            &:hover,
            :focus {
                box-shadow: none;
                background: ${color("common.white")};
                border: 1px solid ${color("common.ui5")};
            }
            min-width: 150px;
            ${IconWrapper} {
                svg {
                    color: ${color("common.ui1")};
                    fill: ${color("common.ui1")};
                }
            }
            ${xScreen("lte", "sm")} {
                width: 100%;
            }
            &:hover ${IconWrapper} {
                animation: none;
            }
        `,
        typographyProps: {
            size: 14,
            weight: 400,
            color: "text.main",
        },
    },
    searchFormStyles: {
        wrapper: {
            css: css`
                margin-bottom: 40px;
                background: ${color("common.white")};
                border: 1px solid ${color("common.ui5")};
                padding: 10px 15px;
                margin-top: -25px;
                position: relative;
                ${GridItemStyle}:has(${DivWrapper}:empty) {
                    display: none;
                }

                ${GridItemStyle}:has(div[data-test-selector="orderHistory_filterDateRange"]) {
                    // Take Full width
                    flex-basis: calc(100% - 100%/3);
                    max-width: calc(100% - 100%/3);
                }

                div[data-test-selector="orderHistory_filterDateRange"] div {
                    width: 100%;
                }

                ${xScreen("lte", "sm")} {
                    ${GridItemStyle}:has(div[data-test-selector="orderHistory_filterDateRange"]) {
                        flex-basis: 100%;
                        max-width: 100%;
                    }
                }
            `,
        },
        headingText: {
            variant: "h5",
            css: css`
                display: none;
            `,
        },
    },
    clearButton: {
        variant: "tertiary",
        sizeVariant: "large",
        typographyProps: {
            size: 16,
            lineHeight: "18px",
            weight: 500,
            color: "common.ui3",
        },
        icon: {
            src: CaretRight,
            position: "right",
        },
        css: css`
            margin-left: auto;
            ${IconWrapper} {
                width: 12px;
                height: 12px;
                margin-left: 4px;
                top: 0;
                svg {
                    color: ${color("common.ui1")};
                    fill: ${color("common.ui1")};
                }
            }
            &:hover {
                ${TypographyStyle}, ${IconWrapper} svg {
                    color: ${color("primary")};
                    fill: ${color("primary")};
                }
            }
        `,
        mergeCss: true,
    },
};

export default myAccountFilterSectionStyles;
